import { ThemeProvider } from "@emotion/react";
import { Box } from "@mui/material";
import { Daphne, initDaphne } from "@syadem/daphne-js";
import { UserManager } from "oidc-client-ts";
import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { AppRouter, Config, CountryConfig } from "../src/appConfig";
import { Apis, initApis } from "../src/network/apis";
import { initQueries, Queries } from "../src/queries";
import { ResultNotifier } from "../src/resultNotifier";
import { initServices, Services } from "../src/services";
import { ServiceBus } from "../src/services/serviceBus";
import { AppStore, initStore } from "../src/store";
import { Loading } from "../src/ui/components/shared/Loading";
import { theme } from "../src/ui/layout/Theme";
import { LoadingAppFailed } from "../src/ui/pages/LoadingAppFailed";
import { DependenciesProvider } from "../src/ui/providers/Dependencies";
import { I18nProvider } from "../src/ui/providers/I18nProvider";
import { routes } from "../src/ui/routes";
import { datamatrixProcessor } from "../src/utils/datamatrix";
import { SecurityNumberInput } from "./components/SecurityNumberInput";
import { validateSecurityNumber } from "./utils/securityNumberValidator";
import "dayjs/locale/el";
import "dayjs/locale/en-gb";
import { AccountActivation } from "./components/AccountActivation";

export class App {
  private readonly router: AppRouter;
  private readonly store: AppStore;
  private readonly apis: Apis;
  private readonly services: Services;
  private readonly queries: Queries;
  private readonly serviceBus: ServiceBus;
  private readonly resultNotifier: ResultNotifier;
  private readonly countryConfig: CountryConfig;
  private daphne?: Daphne;

  constructor(private readonly config: Config) {
    const {
      oidcUrl,
      kairosProApiBasePath,
      kairosTeamApiBasePath,
      kairosCertApiBasePath,
      sadApiBasePath,
      arianeApiBasePath,
      mesvaccinsBaseUrl,
    } = config;

    const oidcManager = initOidcManager(oidcUrl);
    const currentToken = async () => (await oidcManager.getUser())?.access_token ?? "";

    this.store = initStore();
    this.resultNotifier = new ResultNotifier(this.store);

    this.apis = initApis({
      kairosProApiBasePath,
      kairosTeamApiBasePath,
      kairosCertApiBasePath,
      sadApiBasePath,
      arianeApiBasePath,
      accessToken: currentToken,
    });

    this.router = createBrowserRouter(routes({ subscriptionEnabled: false, mesvaccinsBaseUrl: mesvaccinsBaseUrl }));

    this.countryConfig = {
      defaultLocale: "gr",
      availableLocales: ["gr", "en-gb"],
      defaultCountryCode: "GRC",
      zipCode: {
        length: 5,
        regex: /^\d{5}$/,
        placeholder: "ex : 10433",
        sharingPlaceholder: "ex : ΠΟΛΥΤΕΧΝΕΙΟ ΑΘΗΝΑ, 10433, ...",
        requiredForHealthRecord: true,
      },
      phoneNumber: {
        minLength: 10,
        maxLength: 17,
        regex: /^(?:\+30|0030)?\s?[2-9][0-9]{2}\s?[0-9]{3}\s?[0-9]{4}$/,
        placeholder: "ex : +30 215 715 4396",
      },
      securityNumber: {
        validator: validateSecurityNumber,
        Input: SecurityNumberInput,
      },
      AccountActivation: AccountActivation,
    };

    this.queries = initQueries({
      store: this.store,
      apis: this.apis,
    });

    this.services = initServices({
      store: this.store,
      oidcManager,
      router: this.router,
      apis: this.apis,
      queries: this.queries,
      createSubscription: this.countryConfig.subscription?.services.createSubscription,
    });

    this.serviceBus = new ServiceBus(this.services, this.resultNotifier, this.router);
  }

  async start() {
    const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

    root.render(
      <React.StrictMode>
        <I18nProvider defaultLocale={this.countryConfig.defaultLocale}>
          <Box sx={{ width: "100vw", height: "100vh" }}>
            <Loading title={"Authentification en cours..."} />
          </Box>
        </I18nProvider>
      </React.StrictMode>,
    );

    try {
      const [daphne] = await Promise.all([
        initDaphne({ baseUrl: this.config.nuvaUrl, locale: this.countryConfig.defaultLocale }),
        this.services.signIn.call(),
      ]);
      this.daphne = daphne;
    } catch {
      root.render(
        <React.StrictMode>
          <I18nProvider defaultLocale={this.countryConfig.defaultLocale}>
            <LoadingAppFailed />
          </I18nProvider>
        </React.StrictMode>,
      );
      return;
    }

    const authState = this.store.getState().authState;

    if (authState.type != "signed-in") {
      root.render(
        <React.StrictMode>
          <I18nProvider defaultLocale={this.countryConfig.defaultLocale}>
            <LoadingAppFailed />
          </I18nProvider>
        </React.StrictMode>,
      );
      return;
    }

    root.unmount();

    ReactDOM.createRoot(document.getElementById("root")!).render(
      <React.StrictMode>
        <DependenciesProvider
          dependencies={{
            store: this.store,
            arianeApi: this.apis.arianeApi,
            queries: this.queries,
            serviceBus: this.serviceBus,
            sadApi: this.apis.sadApi,
            daphne: this.daphne,
            apis: this.apis,
            config: this.config,
            countryConfig: this.countryConfig,
            processDatamatrixCode: datamatrixProcessor(this.daphne),
          }}
        >
          <ThemeProvider theme={theme}>
            <I18nProvider defaultLocale={this.countryConfig.defaultLocale}>
              <RouterProvider router={this.router} />
            </I18nProvider>
          </ThemeProvider>
        </DependenciesProvider>
      </React.StrictMode>,
    );
  }
}

const OIDC_CLIENT = "kairos-pro-front";

function initOidcManager(oidcUrl: string) {
  const oidcManager = new UserManager({
    client_id: OIDC_CLIENT,
    authority: oidcUrl,
    redirect_uri: window.location.href,
    loadUserInfo: true,
  });
  oidcManager.events.addAccessTokenExpired((error: unknown) => {
    console.debug("Access token expired error", error);
  });
  oidcManager.events.addUserSignedOut(() => {
    alert("User signed out");
  });
  oidcManager.events.addSilentRenewError((error: unknown) => {
    console.debug("Silent renew error", error);
    // reload window to force user to sign in again
    window.location.reload();
  });
  return oidcManager;
}
